import { useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useDeviceEvents, DeviceReading } from '../../hooks/useDeviceEvents';
import axios from '../../utils/axios';

const LABELS: Record<string, string> = {
  TONOREF: 'NIDEK TONOREF II',
  PHOROPTER: 'NIDEK RT-3100',
  LENSMETER: 'NIDEK LM-500',
};

function summary(r: DeviceReading) {
  const d = r.data;
  if (r.deviceType === 'TONOREF') {
    const ar = d.autoRef;
    return `Auto-ref  R ${ar?.right?.sphere} / ${ar?.right?.cylinder} × ${ar?.right?.axis}   L ${ar?.left?.sphere} / ${ar?.left?.cylinder} × ${ar?.left?.axis}   |   IOP  R ${d.iop?.right?.mean} mmHg  L ${d.iop?.left?.mean} mmHg`;
  }
  if (r.deviceType === 'PHOROPTER') {
    return `R ${d.right?.sphere} / ${d.right?.cylinder} × ${d.right?.axis}  VA ${d.right?.va}   L ${d.left?.sphere} / ${d.left?.cylinder} × ${d.left?.axis}  VA ${d.left?.va}`;
  }
  if (r.deviceType === 'LENSMETER') {
    return `R ${d.right?.sphere} / ${d.right?.cylinder} × ${d.right?.axis}  ADD ${d.right?.add}   L ${d.left?.sphere} / ${d.left?.cylinder} × ${d.left?.axis}  ADD ${d.left?.add}   PD ${d.pdBinocular}`;
  }
  return JSON.stringify(d);
}

interface Props {
  /** Filter to only show readings for this patient */
  patientId?: string;
  /** Called after the clinician accepts a reading — use to refresh the form */
  onAccepted?: (reading: DeviceReading) => void;
}

export function DeviceReadingBanner({ patientId, onAccepted }: Props) {
  const [readings, setReadings] = useState<DeviceReading[]>([]);

  useDeviceEvents((r) => {
    if (patientId && r.patientId && r.patientId !== patientId) return;
    setReadings((prev) => [r, ...prev].slice(0, 5));
  });

  const dismiss = (id: number) => setReadings((prev) => prev.filter((r) => r.id !== id));

  const accept = async (r: DeviceReading) => {
    await axios.post(`/device-ingest/${r.id}/accept`);
    onAccepted?.(r);
    dismiss(r.id);
  };

  if (!readings.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {readings.map((r) => (
        <Alert
          key={r.id}
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="contained" onClick={() => accept(r)}>
                Accept
              </Button>
              <Button size="small" onClick={() => dismiss(r.id)}>
                Dismiss
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            <Chip label={LABELS[r.deviceType] ?? r.deviceType} size="small" sx={{ mr: 1 }} />
            New reading
          </Typography>
          <Typography variant="caption">{summary(r)}</Typography>
        </Alert>
      ))}
    </Box>
  );
}
